import styled from 'styled-components';

import { Text } from '@/nova/components';
import { ANIM, COLORS, FEATURE_TONE_COLORS, VS_BAR_COLORS, VS_BAR_PACK } from '@/nova/theme/tokens';

/** Smooth ease for the projection fill + pill when switching tiers (mirrors the configurator's
 *  find-my-pack DetailTrack — the "could be" indicator slides while You/Market stay put). */
const SWITCH = `${ANIM.BAR_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`;

/** Bar height = tier pill (21px) + a 1px fill frame top + 1px bottom, so the pill nests INSIDE
 *  the bar (matches the configurator DetailTrack). */
const TRACK_H = 23;
/** Rounded end-cap radius (half the bar height); the You/Market arrows point at each fill's
 *  cap centre, so the arrow tip nudges left by this. */
export const MARKER_CAP_PX = TRACK_H / 2;
const CAP = MARKER_CAP_PX;

export const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

export const Label = styled(Text)`
  font-size: 12px;
  color: ${COLORS.textSubtle};
`;

/** Holds the track + the absolutely-positioned You/Market markers below it. */
export const TrackArea = styled.div`
  position: relative;
  padding-bottom: 26px;
`;

export const Track = styled.div`
  position: relative;
  /* Own stacking context so the market hatch / projection fill / tier pill z-order resolves
     HERE — the pill (z-index 10) always paints ABOVE the market hatch. */
  isolation: isolate;
  height: ${TRACK_H}px;
  border-radius: 12px;
  background: ${VS_BAR_COLORS.track};
`;

/** Market "range" segment (0 → market%). When $onTop (Could-be ≥ Market) it overlays the
 *  projection fill as a WHITE hatch so the stripes read over the fill; otherwise it sits BEHIND the
 *  projection as a grey hatch, visible only in the Could-be → Market gap. Same two hatches the
 *  shared VsMarketBar and the configurator DetailMarketFill paint. */
export const MarketFill = styled.span<{ $w: number; $onTop: boolean }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: ${({ $onTop }): number => ($onTop ? 3 : 1)};
  width: ${({ $w }): string => `${Math.min(100, Math.max(0, $w))}%`};
  border-radius: 12px;
  background: ${({ $onTop }): string => ($onTop ? VS_BAR_PACK.marketHashOver : VS_BAR_PACK.marketHash)};
  transition: width ${SWITCH};
`;

/** "Could be" projection fill (0 → couldBe%): the selected tier's reach, in the find-my-pack
 *  deep-blue→periwinkle gradient. Slides as the picker changes tier. UNIFORM across features, like
 *  the shared VsMarketBar: the feature's state is stated by the status line and the You marker, so a
 *  fill tinted per tone (what this used to paint) only restated it in a second, competing colour. */
export const ProjectionFill = styled.span<{ $w: number }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 2;
  width: ${({ $w }): string => `${Math.min(100, Math.max(0, $w))}%`};
  border-radius: 12px;
  background: ${VS_BAR_PACK.youFill};
  transition: width ${SWITCH};
`;

/**
 * One flex row across the WHOLE track, holding every pill the bar draws. The pills are px-sized
 * while every position here is a PERCENTAGE, and each attempt to reconcile the two with a threshold
 * put a pill somewhere it must not be — outside the track at one end, on top of the Off pill at the
 * other. Flex layout settles all of it structurally instead:
 *
 *  · pills are SIBLINGS, so they can never be drawn on top of each other, at any width;
 *  · max-width: 100% on a pill is measured against this row, i.e. the track, so no label can take
 *    a pill outside the bar (a long enough one ellipsizes at the track's edge);
 *  · the tier pill's margin-left: auto pins its RIGHT edge to the fill's tip, which the PillGap
 *    after it holds open. Where the fill is too short to hold the label, the gap collapses and the
 *    pill keeps its natural width, extending RIGHTWARDS into empty track instead of shrinking.
 *
 * Measured across track widths, tiers and label lengths: right edge exactly on the tip whenever it
 * fits, never one pixel outside the track, never overlapping the Off pill.
 */
export const PillSlot = styled.span`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 0;
  right: 0;
  z-index: 10;
  display: flex;
  align-items: center;
`;

/** A pill in the row, in two roles (both mirroring the shared VsMarketBar): the "could be" tier
 *  name riding the projection fill's tip ($at — the tip, as a percentage of the track), and
 *  $off, the "You" reference for a feature that is not enabled, stating Off in the inactive tone
 *  at the track's start, the way the product root's cards state it, instead of the unlabelled dot
 *  this bar used to draw there (which said where you stand but never what that state IS). */
export const TierPill = styled.span<{ $off?: boolean; $rides?: boolean; $offPx?: number }>`
  box-sizing: border-box;
  /* NEITHER pill shrinks: flex spreads shrinkage over every shrinkable item, and a fraction of a
     pixel is all text-overflow needs to eat a character. The gap alone yields (see PillGap), and
     the bound below is what stops the pill — measured against the row, i.e. the track, less the
     width the Off pill holds at the start when it is on the track. */
  flex: 0 0 auto;
  min-width: 0;
  /* Bounded by the row (the track), less whatever the Off pill holds at its start. That width is
     MEASURED, not assumed: the Off pill's copy is translated ("Off" is 36px, "Apagado" 62px), so a
     constant was wrong in both directions — past the track in one locale, ellipsizing a label that
     fits with track to spare in the other. */
  max-width: ${({ $offPx = 0 }): string => ($offPx > 0 ? `calc(100% - ${$offPx}px)` : '100%')};
  ${({ $rides }): string => ($rides ? 'margin-left: auto;' : '')}
  overflow: hidden;
  text-overflow: ellipsis;
  border-radius: 12px;
  background: ${({ $off }): string => ($off ? FEATURE_TONE_COLORS.inactive : VS_BAR_PACK.tierBg)};
  padding: 5px 10px;
  font-size: 11px;
  font-weight: 700;
  line-height: 11px;
  white-space: nowrap;
  color: ${({ $off }): string => ($off ? COLORS.onAccent : VS_BAR_PACK.tierAccent)};
`;

/** The empty track between the projection tip and the track's end — see the shared VsMarketBar's
 *  PillGap: sizing the gap, rather than positioning the pill, is what lets the pill grow rightwards
 *  when its fill is too short, and its basis is what animates so the pill rides that fill. */
export const PillGap = styled.span<{ $at: number }>`
  /* The ONLY shrinkable item in the row, so every pixel the could-be pill needs beyond its fill
     comes out of here. */
  flex: 0 1 ${({ $at }): string => `${100 - Math.min(100, Math.max(0, $at))}%`};
  min-width: 0;
  transition: flex-basis ${SWITCH};
`;

/** A pill-anchored marker keeps its ARROW on the pill and lets the LABEL slide when the two cannot
 *  both fit near a track end (labelClampPx) — the same split the shared VsMarketBar makes, and the
 *  reason the pill mode centres unconditionally instead of switching alignment. */
export const MarkerLabelBox = styled.span<{ $shift: number }>`
  position: relative;
  /* Deliberately NOT transitioned, same as the shared bar: measured inert at every width this bar
     renders at, and a net regression on the narrow one. */
  transform: ${({ $shift }): string => `translateX(${$shift}px)`};
`;

export type MarkerAlign = 'center' | 'start' | 'end';

const FLEX_ALIGN: Record<MarkerAlign, string> = {
  center: 'center',
  start: 'flex-start',
  end: 'flex-end',
};

/** The arrow points at the fill's rounded end-cap centre, so the whole column nudges left by CAP;
 *  end right-aligns the column (runs left), start left-aligns it (runs right), center centres
 *  it — so an edge marker's label never gets clipped. */
const markerShift = (align: MarkerAlign): string => {
  if (align === 'end') return `translateX(calc(-100% - ${CAP}px))`;
  if (align === 'start') return `translateX(-${CAP}px)`;
  return `translateX(calc(-50% - ${CAP}px))`;
};

/** Half the arrow's own width (MarkerArrow is 12px wide). start left-aligns the column
 *  (align-items: flex-start), so the arrow's CENTRE sits ARROW_HALF px to the right of the
 *  column's left edge, not ON it - needed to line the arrow up with the pill that marks the
 *  track's start (the "Off" TierPill, which replaced the dot this bar used to draw there). */
const ARROW_HALF = 6;

/** The CAP-px left nudge (in markerShift) assumes there is CAP px of track to its left to nudge
 *  INTO (true for any real fill's right-edge cap). Right at the track's own ends (now/market at 0
 *  or 100) there's no such room, so the raw anchor also needs to stay at least CAP px in from that
 *  edge - otherwise the nudge pushes the arrow past the track boundary entirely (e.g. the "You"
 *  marker rendering outside the bar when now is at the very start).
 *  start's floor is raised a further ARROW_HALF px so the arrow's centre (not its edge) lands
 *  exactly CAP px in from the edge - matching where the start-of-track pill sits, instead of
 *  landing ARROW_HALF px left of it. */
const markerLeft = ($left: number, align: MarkerAlign): string => {
  const pct = `${Math.min(100, Math.max(0, $left))}%`;
  if (align === 'start') return `max(${pct}, ${2 * CAP - ARROW_HALF}px)`;
  if (align === 'end') return `min(${pct}, calc(100% - ${CAP}px))`;
  return pct;
};

/** One marker: its ▲ arrow stacked over its label in a single column, sitting just below the bar. */
/** $onPill anchors the column on the TIER PILL's centre instead of on a fill's end-cap — used for
 *  the "You" marker when the pill sits on the very point it marks, so the two read as one unit. No
 *  cap nudge (there is no cap to aim at) and no edge clamp on the anchor: the pill's centre is
 *  inside the track by construction, and the label slides on its own (see MarkerLabelBox) when the
 *  two cannot both fit near a track end. */
export const Marker = styled.span<{ $left: number; $align: MarkerAlign; $onPill?: boolean }>`
  position: absolute;
  top: ${TRACK_H + 4}px;
  left: ${({ $left, $align, $onPill }): string =>
    $onPill ? `${Math.min(100, Math.max(0, $left))}%` : markerLeft($left, $align)};
  transform: ${({ $align, $onPill }): string => ($onPill ? 'translateX(-50%)' : markerShift($align))};
  display: inline-flex;
  flex-direction: column;
  align-items: ${({ $align, $onPill }): string => ($onPill ? 'center' : FLEX_ALIGN[$align])};
  gap: 2px;
  transition: left ${SWITCH};
`;

/** Rounded-corner up-arrow (fill + stroke share the marker colour, round line-join softens it).
 *  Render with <MarkerArrow viewBox="0 0 12 8"><path d="M6 1.5L10.5 6.5L1.5 6.5Z" /></MarkerArrow>. */
export const MarkerArrow = styled.svg<{ $color: string }>`
  display: block;
  width: 12px;
  height: 8px;
  fill: ${({ $color }): string => $color};
  stroke: ${({ $color }): string => $color};
  stroke-width: 3;
  stroke-linejoin: round;
`;

export const MarkerLabel = styled(Text)<{ $color: string }>`
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  color: ${({ $color }): string => $color};
`;
